import { ChangeDetectionStrategy, Component, inject, input, resource, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { firstValueFrom } from 'rxjs';
import { HistoryRecordService } from 'src/app/service/history-record';

import { ActivityRecord } from './activity-record';

@Component({
  selector: 'lcm-activity-stream',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatTooltipModule, MatButtonModule, MatToolbarModule, ActivityRecord],
  styles: [
    `
      .activity-list {
        max-height: 80vh;
        overflow: auto;
        padding: 1rem;
      }
    `,
  ],
  template: `
    @let pending = isLoading();
    <mat-toolbar>
      <h2>Feed</h2>
      <span class="spacer"></span>

      @if (pending) {
        <mat-icon class="rotating">refresh</mat-icon>
      }
      <span class="spacer"></span>
      @if (limit()) {
        <button matMiniFab (click)="limit.set(0)" [disabled]="pending" matTooltip="Show all recordsResource">
          <mat-icon>more_time</mat-icon>
        </button>
      }
    </mat-toolbar>

    <div
      class="activity-list flex flex--column mat-bg-surface mat-text-on-surface mat-font-body-md"
      data-testid="activity-list"
    >
      @if (pending) {
        <h3>Loading...</h3>
      } @else if (loadError()) {
        <div class="mat-bg-error-container mat-text-on-error-container">
          <h3>Cannot load history</h3>
          <pre style="overflow:auto">{{ loadError() }}</pre>
        </div>
      } @else {
        @for (record of records(); track record.Time) {
          <lcm-activity-record [record]="record" />
        } @empty {
          <h3 class="text-center">There are no any activities here yet.</h3>
        }
      }
    </div>
  `,
})
export class ActivityStream {
  private history = inject(HistoryRecordService);

  id = input.required<string>();

  limit = signal(6);

  private recordsResource = resource({
    params: () => ({ limit: this.limit(), id: this.id() }),

    loader: async ({ params }) => {
      const result = await firstValueFrom(this.history.getHistory(params.id, !params.limit, params.limit));

      return result;
    },
  });

  loadError = this.recordsResource.error;

  records = this.recordsResource.value;

  isLoading = this.recordsResource.isLoading;
}
